import { ref, Ref, MaybeRef, unref, watch } from 'vue'

import { GetContactsQuery, WithPagination, Response, SortableFields } from '@/shared/types/api'

export const usePaginatedData = <T>(
  {
    size,
    fetchData,
    idKey,
    sortByField = 'name',
  }:
  { size: MaybeRef<number>,
    idKey?: string,
    fetchData: (data: GetContactsQuery) => Promise<Response<WithPagination<T[]>> | void>,
    sortByField?: SortableFields,
  }) => {
  const page = ref(0)
  const isLoading = ref(false)
  const total = ref(0)
  const lastPage = ref(0)
  const list = ref<T[]>([]) as Ref<T[]>

  const keysMap: Map<string, true> = new Map()

  const reset = () => {
    list.value = []
    page.value = 0
    total.value = 0
    lastPage.value = 0
    keysMap.clear()
  }

  watch(() => size, async () => {
    reset()
    await fetchNext()
  })

  const fetchNext = async () => {
    if (lastPage.value && page.value >= lastPage.value) {
      return
    }

    page.value++
    try {
      isLoading.value = true
      const response = await fetchData({ Page: page.value, Size: unref(size), SortBy: sortByField })

      // Чтобы проходили тесты, где несильно нужен стор
      if (!response?.data?.page?.total) {
        page.value--
        isLoading.value = false
        return
      }

      if (!idKey && response.data.list?.length) {
        list.value.push(...response.data.list)
      } else if (response.data.list?.length) {
        for (const item of response.data.list) {
          const id = item[idKey as keyof T] as unknown as string
          if (keysMap.has(id)) {
            continue
          }

          keysMap.set(id, true)
          list.value.push(item)
        }
      }

      total.value = response.data.page.total
      lastPage.value = response.data.page.lastPage
    } catch (e) {
      page.value--
      console.error(e)
    } finally {
      isLoading.value = false
    }
  }

  return {
    total,
    lastPage,
    isLoading,
    list,
    page,
    fetchNext,
    reset,
  }
}
